import type { EmployeeSlug } from "@alm/domain/marketing";
import { MEDIA_BASE, type Film } from "./film";

interface FilmFeature {
  film: Film;
  title: string;
  subtitle: string;
}

type FilmFacts = Pick<Film, "title" | "description" | "length" | "seconds" | "isoDuration">;

/** A desk's film, its files named for the slug: 1080 to wide screens, 720 to phones. */
function deskFilm(slug: EmployeeSlug, facts: FilmFacts, uploadDate: string): Film {
  return {
    ...facts,
    uploadDate,
    width: 1920,
    height: 1080,
    poster: `${MEDIA_BASE}/${slug}-film-poster.webp`,
    sources: [
      {
        src: `${MEDIA_BASE}/${slug}-film-1080.mp4`,
        type: "video/mp4",
        media: "(min-width: 768px)",
      },
      { src: `${MEDIA_BASE}/${slug}-film-720.mp4`, type: "video/mp4" },
    ],
  };
}

/** The employees whose page opens on a film, right after the hero. */
export const EMPLOYEE_FILMS: Partial<Record<EmployeeSlug, FilmFeature>> = {
  aria: {
    film: deskFilm(
      "aria",
      {
        title: "Aria, the PEOPLE superagent",
        description:
          "Aria in 70 seconds: an assistant inside the operating system, with your access, who carries a whole job through from one plain-English ask and shows you the plan first.",
        length: "1:09",
        seconds: 69,
        isoDuration: "PT1M9S",
      },
      "2026-10-18",
    ),
    title: "Aria in 70 seconds",
    subtitle:
      "An assistant inside the operating system, with your access. She carries a job through from one plain-English ask, and shows you the plan before anything changes.",
  },
  sarah: {
    film: deskFilm(
      "sarah",
      {
        title: "Sarah, AI Front Desk Receptionist",
        description:
          "Sarah in 60 seconds: calls, texts and visitors answered around the clock, routed to the right desk, clinical questions handed to staff, and every call on one record.",
        length: "0:57",
        seconds: 57,
        isoDuration: "PT57S",
      },
      "2026-10-19",
    ),
    title: "Sarah in 60 seconds",
    subtitle:
      "Calls, texts and visitors answered around the clock and routed to the right desk. Anything clinical goes straight to your nurse, and every call lands on one record.",
  },
  olivia: {
    film: deskFilm(
      "olivia",
      {
        title: "Olivia, AI Staffing Coordinator",
        description:
          "Olivia in 60 seconds: one hiring instruction becomes a posted role, screened applicants and interviews on your calendar, while the interview and the hire stay human.",
        length: "1:00",
        seconds: 60,
        isoDuration: "PT1M",
      },
      "2026-10-19",
    ),
    title: "Olivia in 60 seconds",
    subtitle:
      "One hiring instruction becomes a posted role, screened applicants and interviews on your calendar. The interview and the hiring decision stay yours.",
  },
  nancy: {
    film: deskFilm(
      "nancy",
      {
        title: "Nancy, AI Procurement Manager",
        description:
          "Nancy in 60 seconds: the dishes residents like, a list sized to the pantry and the cheapest cart across three stores, about 30% off the food bill, ordered only when you approve.",
        length: "1:03",
        seconds: 63,
        isoDuration: "PT1M3S",
      },
      "2026-10-19",
    ),
    title: "Nancy in 60 seconds",
    subtitle:
      "The dishes your residents like, a list sized to the pantry and the cheapest cart across three stores: about 30% off the food bill. Nothing is ordered until you approve.",
  },
};
